import { observable } from "@legendapp/state";
import type * as Ably from "ably";

import { syncedCrudPersist } from "~/libs/legend-state";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "../app-data-ready";
import type { DatasetFolder } from "./dataset-folders.types";

/**
 * Org-shared folders used to group datasets on the datasets browse page.
 * A dataset points at zero or one folder via its `folderId`.
 */
export const datasetFolders$ = observable(
  syncedCrudPersist<DatasetFolder>({
    persist: {
      name: "dataset-folders",
    },
    waitFor: appDataReady$,
    list: async () => {
      return await trpc.datasetFolders.list.query();
    },
    update: async ({ id, ...input }) => {
      if (!id) return;
      await trpc.datasetFolders.update.mutate({ id, ...input });
    },
    create: async (input) => {
      return await trpc.datasetFolders.create.mutate(input);
    },
    delete: async (input) => {
      if (!input.id) return;
      await trpc.datasetFolders.delete.mutate({ id: input.id });
    },
    subscribe: ({ update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];
        update({ mode: "merge", value: items });
      };

      channel.subscribe("dataset-folders", handler);

      return () => {
        channel.unsubscribe("dataset-folders", handler);
      };
    },
  }),
);
